import { useApolloClient } from '@apollo/client/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/inputs/Input';
import { Modal } from '../../../components/Modal';
import { useUserRoleTranslations } from '../../../hooks/translations/useUserRoleTranslations';
import { T, useT } from '../../../translation/src';
import { Project, ProjectAccessLevel, UserRole, UserStatus, UsersDocument, useProjectAddGuestMutation, useProjectRemoveGuestMutation, useProjectSetGuestAccessLevelMutation, useUsersQuery } from '../../../types/graphqlTypes';
import { AccessLevelSelect, DEFAULT_ACCESS_LEVEL } from '../ProjectAccessPicker';
import { CheckIcon } from '@heroicons/react/24/outline';

const useTranslations = () => ({
  accessTo: useT('access to {name}', { swc: true }),
  searchUsers: useT('search users', { swc: true }),
  save: useT('save', { swc: true }),
  cancel: useT('cancel', { swc: true }),
});

interface ProjectAccessOverviewModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name'>;
}

/** Guest id → level; absent = no access. */
type GuestSelection = ReadonlyMap<string, ProjectAccessLevel>;

/**
 * Who can open this project, from the project's side. Admins and members always can (that is
 * what their role means, shown read-only); guests are per project with a level, the same switch
 * as on the users page. Saved as a diff, like the per-guest dialog.
 */
const ProjectAccessOverviewModal_: React.FC2<ProjectAccessOverviewModalProps> = ({ onClose, open, project }) => {
  const translations = useTranslations();
  const roleTranslations = useUserRoleTranslations();
  const { organisationId = '' } = useParams();
  const apolloClient = useApolloClient();
  const { data: usersData, loading } = useUsersQuery({ variables: { organisationId }, skip: !open });
  const [addProjectGuest] = useProjectAddGuestMutation();
  const [removeProjectGuest] = useProjectRemoveGuestMutation();
  const [setGuestAccessLevel] = useProjectSetGuestAccessLevelMutation();

  const users = useMemo(() => (usersData?.usersByOrganisationId ?? []).filter((user) => user.role !== UserRole.ApiKey).slice().sort((a, b) => a.name.localeCompare(b.name)), [usersData?.usersByOrganisationId]);
  const guests = useMemo(() => users.filter((user) => user.role === UserRole.Guest), [users]);
  /** Guests' access as stored right now; the rows diff against this on save. */
  const initial = useMemo<GuestSelection>(() => new Map(guests.flatMap((guest) => guest.projectAccess.filter((access) => access.projectId === project.id).map((access) => [guest.id, access.level] as const))), [guests, project.id]);

  const [edited, setEdited] = useState<GuestSelection | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Until the admin touches a row, the selection mirrors the data (which may still be loading).
  const selection = edited ?? initial;

  const [openedFor, setOpenedFor] = useState(open);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) {
      setEdited(null);
      setSearch('');
    }
  }

  const visibleUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? users.filter((user) => user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle)) : users;
  }, [users, search]);

  const update = useCallback(
    (mutate: (next: Map<string, ProjectAccessLevel>) => void) => {
      const next = new Map(selection);
      mutate(next);
      setEdited(next);
    },
    [selection],
  );
  const setLevel = useCallback((guestId: string, level: ProjectAccessLevel | undefined) => update((next) => (level === undefined ? next.delete(guestId) : next.set(guestId, level))), [update]);

  const changes = useMemo(() => {
    const added = guests.filter((guest) => selection.has(guest.id) && !initial.has(guest.id));
    const removed = guests.filter((guest) => !selection.has(guest.id) && initial.has(guest.id));
    const relevelled = guests.filter((guest) => selection.has(guest.id) && initial.has(guest.id) && selection.get(guest.id) !== initial.get(guest.id));
    return { added, removed, relevelled, count: added.length + removed.length + relevelled.length };
  }, [guests, selection, initial]);

  const onSave = useCallback(async () => {
    if (!changes.count) return;
    setSaving(true);
    try {
      await Promise.all([
        ...changes.removed.map((guest) => removeProjectGuest({ variables: { projectId: project.id, userId: guest.id } })),
        ...changes.added.map((guest) => addProjectGuest({ variables: { projectId: project.id, email: guest.email, level: selection.get(guest.id) } })),
        ...changes.relevelled.map((guest) => setGuestAccessLevel({ variables: { projectId: project.id, userId: guest.id, level: selection.get(guest.id) ?? DEFAULT_ACCESS_LEVEL } })),
      ]);
      await apolloClient.refetchQueries({ include: [UsersDocument] });
      onClose();
    } finally {
      setSaving(false);
    }
  }, [changes, selection, removeProjectGuest, addProjectGuest, setGuestAccessLevel, project.id, apolloClient, onClose]);

  const withAccess = users.filter((user) => user.role !== UserRole.Guest).length + selection.size;

  return (
    <Modal.Container onClose={onClose} open={open} panelClassName="w-full max-w-2xl">
      <Modal.Header onClose={onClose} className="!mb-4">
        <span className="flex flex-col">
          <span>{translations.accessTo.replace('{name}', project.name)}</span>
          <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
            <T _str="{count} of {total} users can open this project" args={{ count: withAccess, total: users.length }} />
          </span>
        </span>
      </Modal.Header>
      <Modal.Body>
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={translations.searchUsers} aria-label={translations.searchUsers} className="w-full !pl-9" />
        </div>
        <div className="overflow-x-hidden overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200 dark:border-gray-700 dark:divide-gray-700 max-h-[55vh] dark:[color-scheme:dark]">
          {visibleUsers.map((user) => {
            const isGuest = user.role === UserRole.Guest;
            const level = selection.get(user.id);
            const pending = user.status === UserStatus.InvitePending;
            return (
              <div key={user.id} className={classNames('flex items-center gap-3 px-3 py-2', isGuest && 'hover:bg-gray-50 dark:hover:bg-[#2c2c2c]')}>
                <div className="flex flex-col flex-grow min-w-0">
                  <span className="text-sm font-medium truncate">
                    {user.name}
                    {pending && (
                      <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                        (<T _str="invite pending" />)
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500 truncate dark:text-gray-400">{user.email}</span>
                </div>
                <span className="text-xs text-gray-600 w-14 shrink-0 dark:text-gray-400">{roleTranslations[user.role.toLowerCase()]}</span>
                <span className="flex justify-end w-32 shrink-0">
                  {isGuest ? (
                    <AccessLevelSelect value={level} onChange={(next) => setLevel(user.id, next)} className="w-full" />
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-neon-green-700 dark:text-neon-green-300">
                      <CheckIcon className="w-4 h-4" />
                      <T _str="full access" swc />
                    </span>
                  )}
                </span>
              </div>
            );
          })}
          {!loading && !visibleUsers.length && (
            <div className="py-8 text-sm text-center text-gray-500 dark:text-gray-400">
              <T _str="No users match your search." />
            </div>
          )}
        </div>
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          <T _str="Admins and members can open and edit every project of the organisation. Guests only see the projects ticked here, at the chosen level. Use Invite to add a guest who is not in the organisation yet." />
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end gap-3">
        <Button variant="secondary" size="xs" onClick={onClose} disabled={saving}>
          <span className="whitespace-nowrap">{translations.cancel}</span>
        </Button>
        <Button variant="primary" size="xs" onClick={onSave} loading={saving} disabled={!changes.count}>
          <span className="whitespace-nowrap">
            {translations.save}
            {changes.count > 0 && ` (${changes.count})`}
          </span>
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

export const ProjectAccessOverviewModal = memo(ProjectAccessOverviewModal_);
